import { create, tsx } from '@dojo/framework/core/vdom';
import { Link } from '@dojo/framework/routing/Link';
import { ConnectStrip } from '../../widgets/ConnectStrip';
import { Head } from '../../widgets/Head';
import { Hero } from '../../widgets/Hero';
import { Summary } from '../../widgets/Summary';

import ContentWrapper from '../../widgets/ContentWrapper';
import Section from '../../widgets/Section';
import Image from '../../widgets/Image';
import Text from '../../widgets/Text';
import IndustryExpertise from '../../widgets/IndustryExpertise';
import SummaryList from '../../widgets/SummaryList';

const codeBlocksImg = require('../../assets/images/illustration/typescript-code-blocks.svg');
const codeReuseImg = require('../../assets/images/illustration/code-reuse.svg');
const engineeringImg = require('../../assets/images/illustration/engineering.svg');
const performanceImg = require('../../assets/images/illustration/high-performance.svg');
const gearsImg = require('../../assets/images/illustration/typescript-gears.svg');
const softwareCycleImg = require('../../assets/images/illustration/software-cycle.svg');

const leadingTop = require('../../assets/images/icon-illustration/cloud-app.svg');
const leadingMiddle = require('../../assets/images/icon-illustration/design-pen.svg');
const leadingBottom = require('../../assets/images/icon-illustration/cloud-idea.svg');
const trailingTop = require('../../assets/images/icon-illustration/plan-gear.svg');
const trailingMiddle = require('../../assets/images/icon-illustration/code-plan.svg');
const trailingBottom = require('../../assets/images/icon-illustration/improve.svg');

const factory = create();

export const TypeScriptDevelopment = factory(function TypeScriptDevelopment() {
	return (
		<div>
			<Head
				title="Typescript Web Development Services | SitePen"
				description="Our typescript web development company builds enterprise solutions to improve processes, streamline operations, and empower business success."
			/>

			<Hero background="level3Blue">
				{{
					xlTitle: (
						<div>
							TypeScript
							<br />
							Web Development
						</div>
					),
					blurb:
						'SitePen provides industry expertise in TypeScript web development and TypeScript development services for enterprise software applications and startups alike.'
				}}
			</Hero>

			<Section>
				<ContentWrapper>
					<SummaryList
						trailingBias="medium"
						listGuttering="none"
						style="alternating-reverse"
						itemPaddingBottom="md"
					>
						{{
							items: [
								{
									title: 'Is TypeScript Development Right for Your Needs?',
									blurb: (
										<virtual>
											<p>
												To answer this question we first need to understand
												what TypeScript is. In summary TypeScript is a
												programming language developed by Microsoft in 2012.
												It was designed to address the challenges of using
												JavaScript to perform development for large
												applications such as those used by enterprises.
												Microsoft developed it while trying to solve their
												own internal development needs and later released it
												to the public.
											</p>
											<p>
												TypeScript, sometimes abbreviated simply as "TS" was
												so successful that even rival Google used it to
												develop the Angular framework for JavaScript.
												TypeScript is a superset of JavaScript allowing it
												to have full compatibility with JavaScript
												libraries, frameworks and applications and can be
												used to create software with React, Vue, Node or any
												other technology within the JS ecosystem.
											</p>
										</virtual>
									),
									image: <Image src={codeBlocksImg} size="lg" />
								},
								{
									blurb: (
										<virtual>
											<p>
												In general, most clients may not necessarily specify
												specific expertise in TypeScript development unless
												they have an existing project written specifically
												in this language. TypeScript developers prefer a
												TypeScript development environment because it is
												highly scalable and has specialized tools for syntax
												and debugging, which are not available in
												JavaScript. This simplifies development for large
												codebases which might contain JS based errors that
												would otherwise remain undetected.
											</p>
											<Text type="body" as="span">
												TypeScript cannot be rendered or run in a browser or
												server. It must be compiled down into JavaScript in
												a process called{' '}
											</Text>
											<Text type="body" as="span" italic>
												transpiling
											</Text>
											<Text type="body" as="span">
												. Therefore, all programs created and written in
												TypeScript are actually run in JavaScript, ensuring
												compatibility with the existing ecosystem of
												JavaScript engines.
											</Text>
										</virtual>
									),
									image: <Image src={gearsImg} lazy size="lg" />
								}
							]
						}}
					</SummaryList>
				</ContentWrapper>
			</Section>
			<Section shaded>
				<ContentWrapper>
					<SummaryList
						trailingBias="medium"
						listGuttering="none"
						style="alternating-reverse"
						itemPaddingBottom="md"
					>
						{{
							items: [
								{
									title: 'Enterprise App Development',
									blurb: (
										<virtual>
											<p>
												For globally recognized brands, corporate clientele,
												and large businesses, TypeScript development is an
												ideal approach to large scale application
												development. SitePen is an expert in TypeScript
												development services and has a{' '}
												<Link to="expertise">wide range of expertise</Link>{' '}
												deploying TypeScript solutions for enterprise
												clientele.
											</p>
											<p>
												TypeScript is an ideal development platform for
												enterprise applications because of its compatibility
												with the full ecosystem of JavaScript libraries and
												frameworks, while providing a more robust
												development environment for large applications.
												Since JavaScript is used for everything from native
												apps, websites, and backend systems, TypeScript is
												an ideal choice to build platforms as a superset to
												JavaScript.
											</p>
										</virtual>
									),
									image: <Image src={codeReuseImg} lazy size="lg" />
								},
								{
									blurb: (
										<virtual>
											<p>
												As a large brand or corporation, you may have an
												entire suite of software applications ranging from
												consumer mobile apps, business productivity and
												internal applications, a client-facing website,
												employee web-portal, and a comprehensive monolithic
												backend system to power the entire ecosystem.
												Because of the sheer volume of different development
												languages, it is possible for each of these
												components to be developed in an entirely different
												codebase, requiring vast teams with disparate
												expertise to scale, enhance or even maintain the
												infrastructure.
											</p>
											<p>
												TypeScript presents an ideal solution for enterprise
												application development. It allows for a single
												development codebase to be utilized and deployed
												across all technology properties. SitePen can
												provide the proper planning, architecture and
												expertise to refactor your code and develop you out
												of technical debt.
											</p>
										</virtual>
									),
									image: <Image src={performanceImg} lazy size="lg" />
								}
							]
						}}
					</SummaryList>
				</ContentWrapper>
			</Section>
			<Section>
				<ContentWrapper size="small">
					<Summary stack>
						{{
							image: <Image src={softwareCycleImg} lazy />,
							title: 'Our TypeScript Development Process',
							blurb: (
								<virtual>
									<p>
										Before a TypeScript application can be developed, we must
										understand the needs of our client first. We will first
										start by working with you or your team to perform a formal
										requirements gathering session. This will involve scoping
										the application out from a feature and expected
										functionality standpoint. Once we have compiled the proper
										feature and functionality list we may wireframe these out to
										solidify the user interface and get on the same page with
										you as the client.
									</p>
									<p>
										In some cases, we may be drawing up specifications for an
										application that runs on an existing platform, refactoring a
										backend system, or adding to or enhancing upon one. In these
										cases a user interface design and wireframing process is not
										needed, as we may continue to rely on the existing
										interfaces that your company is currently running on.
									</p>
									<p>
										We can work closely with your enterprise software
										development teams, or a single point of contact such as a
										technical lead, product manager or project manager.
										TypeScript web development can occur as a staff augmentation
										arrangement, involving working closely with your teams, or
										as a full standalone effort where our teams build out the
										entire system, exclusively.
									</p>
								</virtual>
							)
						}}
					</Summary>
				</ContentWrapper>
			</Section>
			<Section shaded>
				<IndustryExpertise
					icons={{
						leadingBottom,
						leadingMiddle,
						leadingTop,
						trailingBottom,
						trailingMiddle,
						trailingTop
					}}
				>
					{{
						title: 'Is TypeScript Frontend or Backend?',
						content: (
							<virtual>
								<Text type="body">
									TypeScript is neither a frontend or backend language, but rather
									a superset of the already established and well-known software
									language, JavaScript. That being said, since JavaScript can be
									used for both the front end and backend, and since TypeScript
									compiles into JavaScript, then it can be said and understood
									that TypeScript supports both the front end and backend. The
									distinction here is that the front end browser or backend
									webserver do not actually run TypeScript natively.
								</Text>
								<Text type="body">
									The beauty of TypeScript is that it works wherever JavaScript is
									already used, and JavaScript is one of the most versatile
									software languages used in the modern development world.
									TypeScript can be used to create mobile applications, websites,
									web applications, desktop applications, backend systems and
									applications, and many other types of programs. In this way, the
									possibilities are nearly limitless, but the primary benefit is a
									single development approach that is enterprise-ready, and
									results in a true cross-platform solution.
								</Text>
								<Text type="body">
									The result is that large scale projects can be designed and
									built by a team of more tightly focused developers, nearly
									negating the need to staff multiple teams managing entirely
									separate codebases, and requiring very carefully coordinated
									deployment processes. This ultimately reduces the cost of
									development, enhancements and maintenance without any necessary
									reduction in the robustness or scale of the software technology.
								</Text>
							</virtual>
						)
					}}
				</IndustryExpertise>
			</Section>
			<Section>
				<ContentWrapper size="small">
					<Summary stack>
						{{
							image: <Image src={engineeringImg} lazy size="lg" />,
							title: 'TypeScript Consultation',
							blurb: (
								<virtual>
									<p>
										SitePen staffs a team of highly capable TypeScript
										developers. We encourage you to reach out to us to set up a
										consulting session to learn more about why TypeScript might
										be an ideal fit for your organization. While this page is
										dedicated to explaining our expertise and rationale behind
										TypeScript web development services, we do not believe in
										forcing any of our potential clients down a specific path.
										We are willing and able to find the optimal solution for
										your organization’s specific needs.
									</p>
									<p>
										We would love to learn more about your project. TypeScript
										can help save a significant amount in costs and is an ideal
										solution for a ground up development project or as a
										solution to refactor existing code. We can help migrate your
										current platform should the need arise.
									</p>
									<p>
										We offer a quick turnaround on quotes, and provide some of
										the most competitive rates and prices in our market. While
										other companies utilize bulky, and dated software languages,
										SitePen operates entirely within a lean ecosystem of light
										client but highly robust frameworks.
									</p>
								</virtual>
							)
						}}
					</Summary>
				</ContentWrapper>
			</Section>
			<Section type="trailing" shaded>
				<ConnectStrip trailing />
			</Section>
		</div>
	);
});

export default TypeScriptDevelopment;
